/* ═══════════════════════════════════════════════════════════
   ThemeContext — 3-theme system: Dark / Light / Midnight
   CSS-driven via [data-theme="light"|"midnight"] in global.css.
   Persists preference to localStorage.
   ═══════════════════════════════════════════════════════════ */
import React, { createContext, useContext, useState, useEffect } from "react";

const THEMES = ["dark", "light", "midnight"];
const STORAGE_KEY = "sl_theme";

const ThemeContext = createContext({
  theme: "dark",
  isDark: true,
  toggleTheme: () => {},
  setTheme: () => {},
});

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return THEMES.includes(saved) ? saved : "dark";
    } catch { return "dark"; }
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.removeAttribute("data-theme");
    } else {
      root.setAttribute("data-theme", theme);
    }
    try { localStorage.setItem(STORAGE_KEY, theme); } catch {}
  }, [theme]);

  const toggleTheme = () => setThemeState(t => {
    const idx = THEMES.indexOf(t);
    return THEMES[(idx + 1) % THEMES.length];
  });

  const setTheme = (t) => { if (THEMES.includes(t)) setThemeState(t); };

  const isDark = theme !== "light";

  return (
    <ThemeContext.Provider value={{ theme, isDark, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() { return useContext(ThemeContext); }
